import axios from "axios";
import { getAccessToken } from "./authSession";
import { API_BASE_URL } from "../config";

const getAuthHeaders = () => {
    const token = getAccessToken();
    if (!token) {
        window.location.href = "/";
        return {};
    }
    return {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    };
};

const getMultipartAuthHeaders = () => {
    const token = getAccessToken();
    if (!token) {
        window.location.href = "/";
        return {};
    }
    return {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
};

export const listPurchasingSuppliers = async () => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/wholesale-suppliers`, getAuthHeaders());
    return response.data;
};

export const updatePurchasingSupplierSettings = async (supplierId, payload) => {
    const response = await axios.put(
        `${API_BASE_URL}/purchasing/wholesale-suppliers/${supplierId}/purchase-settings`,
        payload,
        getAuthHeaders(),
    );
    return response.data;
};

export const listPriceListUploads = async (params = {}) => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/price-lists/uploads`, {
        ...getAuthHeaders(),
        params,
    });
    return response.data;
};

export const uploadPriceList = async ({ supplierId, parserCode, file }) => {
    const formData = new FormData();
    formData.append("supplier_id", supplierId);
    if (parserCode) {
        formData.append("parser_code", parserCode);
    }
    formData.append("file", file);
    const response = await axios.post(
        `${API_BASE_URL}/purchasing/price-lists/uploads`,
        formData,
        getMultipartAuthHeaders(),
    );
    return response.data;
};

export const parsePriceListUpload = async (uploadId) => {
    const response = await axios.post(
        `${API_BASE_URL}/purchasing/price-lists/uploads/${uploadId}/parse`,
        {},
        getAuthHeaders(),
    );
    return response.data;
};

export const listPriceListItems = async (uploadId, params = {}) => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/price-lists/uploads/${uploadId}/items`, {
        ...getAuthHeaders(),
        params,
    });
    return response.data;
};

export const downloadUnmappedPriceListItems = async (uploadId) => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/price-lists/uploads/${uploadId}/unmapped.xlsx`, {
        ...getAuthHeaders(),
        responseType: "blob",
    });
    return response.data;
};

export const updatePriceListItemMapping = async (itemId, payload) => {
    const response = await axios.put(
        `${API_BASE_URL}/purchasing/price-list-items/${itemId}/mapping`,
        payload,
        getAuthHeaders(),
    );
    return response.data;
};

export const applyPriceListCurrent = async (uploadId) => {
    const response = await axios.post(
        `${API_BASE_URL}/purchasing/price-lists/uploads/${uploadId}/apply-current`,
        {},
        getAuthHeaders(),
    );
    return response.data;
};

export const refreshSnimportPriceList = async (supplierId) => {
    const response = await axios.post(
        `${API_BASE_URL}/purchasing/wholesale-suppliers/${supplierId}/snimport/refresh`,
        {},
        getAuthHeaders(),
    );
    return response.data;
};

export const listCurrentPrices = async (params = {}) => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/current-prices`, {
        ...getAuthHeaders(),
        params,
    });
    return response.data;
};

export const listTopSalesUploads = async (params = {}) => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/top-sales/uploads`, {
        ...getAuthHeaders(),
        params,
    });
    return response.data;
};

export const uploadTopSales = async ({ dateFrom, dateTo, file }) => {
    const formData = new FormData();
    formData.append("date_from", dateFrom);
    formData.append("date_to", dateTo);
    formData.append("file", file);
    const response = await axios.post(
        `${API_BASE_URL}/purchasing/top-sales/uploads`,
        formData,
        getMultipartAuthHeaders(),
    );
    return response.data;
};

export const parseTopSalesUpload = async (uploadId) => {
    const response = await axios.post(
        `${API_BASE_URL}/purchasing/top-sales/uploads/${uploadId}/parse`,
        {},
        getAuthHeaders(),
    );
    return response.data;
};

export const analyzeTopSalesUpload = async (uploadId) => {
    const response = await axios.post(
        `${API_BASE_URL}/purchasing/top-sales/uploads/${uploadId}/analyze`,
        {},
        getAuthHeaders(),
    );
    return response.data;
};

export const listTopSalesItems = async (uploadId, params = {}) => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/top-sales/uploads/${uploadId}/items`, {
        ...getAuthHeaders(),
        params,
    });
    return response.data;
};

export const getTopSalesSummary = async (uploadId) => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/top-sales/uploads/${uploadId}/summary`, getAuthHeaders());
    return response.data;
};

export const getPlanningDefaults = async () => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/planning/settings/defaults`, getAuthHeaders());
    return response.data;
};

export const listPlanningRuns = async (params = {}) => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/planning/runs`, {
        ...getAuthHeaders(),
        params,
    });
    return response.data;
};

export const createPlanningRun = async (payload) => {
    const response = await axios.post(`${API_BASE_URL}/purchasing/planning/runs`, payload, getAuthHeaders());
    return response.data;
};

export const recalculatePlanningRun = async (runId, payload = {}) => {
    const response = await axios.post(`${API_BASE_URL}/purchasing/planning/runs/${runId}/recalculate`, payload, getAuthHeaders());
    return response.data;
};

export const listPlanningItems = async (runId, params = {}) => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/planning/runs/${runId}/items`, {
        ...getAuthHeaders(),
        params,
    });
    return response.data;
};

export const getPlanningSummary = async (runId) => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/planning/runs/${runId}/summary`, getAuthHeaders());
    return response.data;
};

export const previewPlanningPriceAllocation = async (runId) => {
    const response = await axios.post(`${API_BASE_URL}/purchasing/planning/runs/${runId}/preview-price-allocation`, {}, getAuthHeaders());
    return response.data;
};

export const applyPlanningPriceTender = async (runId) => {
    const response = await axios.post(`${API_BASE_URL}/purchasing/planning/runs/${runId}/price-tender`, {}, getAuthHeaders());
    return response.data;
};

export const applyPlanningMinPurchaseTender = async (runId) => {
    const response = await axios.post(`${API_BASE_URL}/purchasing/planning/runs/${runId}/min-purchase-tender`, {}, getAuthHeaders());
    return response.data;
};

export const getPlanningAllocationSummary = async (runId) => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/planning/runs/${runId}/allocation-summary`, getAuthHeaders());
    return response.data;
};

export const createPlanningSupplierOrders = async (runId, payload = {}) => {
    const response = await axios.post(`${API_BASE_URL}/purchasing/planning/runs/${runId}/supplier-orders`, payload, getAuthHeaders());
    return response.data;
};

export const listPlanningSupplierOrders = async (runId) => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/planning/runs/${runId}/supplier-orders`, getAuthHeaders());
    return response.data;
};

export const exportPlanningSupplierOrderExcel = async (supplierOrderId) => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/planning/supplier-orders/${supplierOrderId}/export`, {
        ...getAuthHeaders(),
        responseType: "blob",
    });
    return response;
};

export const previewPlanningSupplierOrderExport = async (supplierOrderId) => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/planning/supplier-orders/${supplierOrderId}/export-preview`, getAuthHeaders());
    return response.data;
};

export const getPlanningSupplierOrderSnimportCartSync = async (supplierOrderId) => {
    const response = await axios.get(
        `${API_BASE_URL}/purchasing/planning/supplier-orders/${supplierOrderId}/snimport/cart-syncs/latest`,
        getAuthHeaders(),
    );
    return response.data;
};

export const sendPlanningSupplierOrderToSnimportCart = async (supplierOrderId) => {
    const response = await axios.post(
        `${API_BASE_URL}/purchasing/planning/supplier-orders/${supplierOrderId}/snimport/cart`,
        {},
        getAuthHeaders(),
    );
    return response.data;
};

export const updatePlanningItem = async (itemId, payload) => {
    const response = await axios.put(`${API_BASE_URL}/purchasing/planning/items/${itemId}`, payload, getAuthHeaders());
    return response.data;
};

export const bulkUpdatePlanningItems = async (runId, payload) => {
    const response = await axios.put(`${API_BASE_URL}/purchasing/planning/runs/${runId}/items/bulk`, payload, getAuthHeaders());
    return response.data;
};

export const listPlanningExclusions = async (params = {}) => {
    const response = await axios.get(`${API_BASE_URL}/purchasing/planning/exclusions`, {
        ...getAuthHeaders(),
        params,
    });
    return response.data;
};

export const upsertPlanningExclusion = async (payload) => {
    const response = await axios.put(`${API_BASE_URL}/purchasing/planning/exclusions`, payload, getAuthHeaders());
    return response.data;
};

export const getPlanningBrands = async (supplierIds, inStockOnly) => {
    const params = new URLSearchParams();
    supplierIds.forEach(id => params.append("supplier_ids", id));
    params.set("in_stock_only", String(inStockOnly));
    const response = await axios.get(`${API_BASE_URL}/purchasing/planning/brands?${params}`, getAuthHeaders());
    return response.data;
};
